import { type CurrencyCode, type Locale } from '@debts-system/contracts';
import { Decimal } from 'decimal.js';

/**
 * Display formatting. Values arrive from the API as decimal strings and are
 * only ever converted to a number at the very last step, inside Intl, so no
 * arithmetic is ever done in floating point.
 */

/** Latin digits everywhere: an exchange's numbers must be unambiguous. */
const NUMBERING = 'latn';

export function formatAmount(
  value: string,
  locale: Locale,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  const decimal = new Decimal(value);
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(decimal.toNumber());
}

export function formatCurrency(
  value: string,
  currency: CurrencyCode,
  locale: Locale,
  fractionDigits = 2,
): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(new Decimal(value).toNumber());
}

/** Rates need more decimals than money, and never fewer. */
export function formatRate(value: string, locale: Locale, fractionDigits = 4): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(new Decimal(value).toNumber());
}

/**
 * The organisation's display timezone. Every business timestamp renders in it,
 * never in the browser's own zone. It mirrors the API's `ORGANIZATION_TIMEZONE`
 * default; the web app has no runtime source for it.
 */
export const DISPLAY_TIMEZONE = 'Asia/Amman';

/**
 * ui-contract §7: dates render `dd/MM/yyyy`, date-times `dd/MM/yyyy hh:mm AM`
 * or `PM` - one fixed, unambiguous, Latin-digit, 12-hour format regardless of
 * locale (`AM`/`PM` stay Latin in Arabic too). `Intl`'s `dateStyle`/`timeStyle`
 * presets are locale-shaped (reordered fields, Arabic digits, ص/م), so this asks
 * `Intl` only for the zone-converted parts and assembles the string itself.
 * `locale` stays in the signature for call-site stability; every render site
 * also wraps the value in its own `dir="ltr"` isolate so it is never reordered
 * inside a surrounding Arabic table cell.
 */
function twelveHourParts(date: Date, timezone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat(`en-US-u-nu-${NUMBERING}`, {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h12',
  }).formatToParts(date);
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function assembleDate(p: Record<string, string>): string {
  return `${p['day']}/${p['month']}/${p['year']}`;
}

function assembleTime(p: Record<string, string>): string {
  return `${p['hour']}:${p['minute']} ${p['dayPeriod']?.toUpperCase()}`;
}

/** Keeps a Latin value reading left-to-right inside a surrounding RTL sentence. */
export function ltrIsolate(value: string): string {
  return `\u2066${value}\u2069`;
}

/** `dd/MM/yyyy hh:mm AM|PM` in `timezone` (default: the organisation's). */
export function formatZonedDateTime(
  value: string | Date,
  _locale: string,
  timezone: string = DISPLAY_TIMEZONE,
): string {
  const p = twelveHourParts(toDate(value), timezone);
  return `${assembleDate(p)} ${assembleTime(p)}`;
}

export function formatDateTime(value: string | Date, locale: Locale): string {
  return formatZonedDateTime(value, locale, DISPLAY_TIMEZONE);
}

/** `hh:mm AM|PM` in the organisation's timezone. */
export function formatTime(value: string | Date, _locale: string = 'ar'): string {
  return assembleTime(twelveHourParts(toDate(value), DISPLAY_TIMEZONE));
}

/** `dd/MM/yyyy` of the instant's calendar day in the organisation's timezone. */
export function formatDate(value: string | Date, _locale: Locale): string {
  return assembleDate(twelveHourParts(toDate(value), DISPLAY_TIMEZONE));
}

/**
 * Rule N2: this module is the only place that constructs an `Intl` formatter.
 * The workspace screens grew their own parallel implementations; they now
 * delegate here so there is one numbering, one calendar and one place to audit.
 */

/** Counts and quantities in the active language, always with Latin digits. */
export function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`).format(value);
}

/** A region name in the active language, for country pickers and labels. */
export function formatRegionName(code: string, locale: string): string {
  return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
}

/**
 * Splits an instant into fixed-width calendar parts in a named timezone, for
 * building a query boundary — not for display. The numbering subtag is not
 * cosmetic here: a runtime defaulting to Arabic-Indic digits would produce
 * `٢٠٢٦` and silently corrupt the assembled string.
 */
export function zonedDateParts(
  value: Date,
  timezone: string,
): Record<Intl.DateTimeFormatPartTypes, string> {
  const parts = new Intl.DateTimeFormat(`en-GB-u-nu-${NUMBERING}`, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(value);
  return Object.fromEntries(parts.map((part) => [part.type, part.value])) as Record<
    Intl.DateTimeFormatPartTypes,
    string
  >;
}

/**
 * The employee of a record in the current language (T1): the other language
 * when one is missing, and "—" when the employee is unknown. Never an id.
 */
export function employeeName(
  employee: { nameAr: string; nameEn: string } | null | undefined,
  locale: string,
): string {
  if (!employee) return '—';
  const first = locale === 'ar' ? employee.nameAr : employee.nameEn;
  const second = locale === 'ar' ? employee.nameEn : employee.nameAr;
  return first || second || '—';
}

/**
 * debts.md section 3 / ui-contract.md section 7 (D14): a JOD amount with
 * exactly five decimals followed by the code, `1,250.00000 JOD`, in both
 * languages. Render sites keep it left-to-right (`<bdi dir="ltr">`).
 */
export function formatJod(value: string, locale: string): string {
  return `${formatJodPlain(value, locale)} JOD`;
}

/** The five-decimal figure alone, for text that already names the currency. */
export function formatJodPlain(value: string, locale: string): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  }).format(new Decimal(value).toNumber());
}

/** A business date (`YYYY-MM-DD`) as `dd/MM/yyyy`, with no time-zone conversion. */
export function formatBusinessDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/** The `YYYY-MM-DD` calendar day of an instant in the organisation's timezone. */
export function zonedDay(value: Date, timezone: string = DISPLAY_TIMEZONE): string {
  const p = zonedDateParts(value, timezone);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * The value of an `<input type="datetime-local">` (`YYYY-MM-DDTHH:mm`) showing
 * an instant as Asia/Amman wall-clock time, whatever the browser's own zone.
 */
export function toZonedInputValue(value: Date, timezone: string = DISPLAY_TIMEZONE): string {
  const p = zonedDateParts(value, timezone);
  // Some runtimes spell midnight "24" in the h23 cycle.
  const hour = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

/**
 * The instant of an Asia/Amman wall-clock `YYYY-MM-DDTHH:mm` value, as an
 * ISO-8601 string with the zone's offset (`2026-10-13T09:15:00+03:00`), or
 * null when the text is not such a value.
 */
export function zonedInputToIso(value: string, timezone: string = DISPLAY_TIMEZONE): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  // The zone's offset at that moment: the wall clock it shows for the
  // UTC instant with the same digits, minus those digits. One correction
  // step is exact outside the hour of a clock change.
  const offsetAt = (instant: number): number => {
    const p = zonedDateParts(new Date(instant), timezone);
    const shown = Date.UTC(
      Number(p.year),
      Number(p.month) - 1,
      Number(p.day),
      Number(p.hour) % 24,
      Number(p.minute),
    );
    return shown - instant;
  };
  const firstGuess = wallAsUtc - offsetAt(wallAsUtc);
  const offsetMs = offsetAt(firstGuess);
  const sign = offsetMs < 0 ? '-' : '+';
  const minutes = Math.abs(Math.round(offsetMs / 60_000));
  const offset = `${sign}${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:00${offset}`;
}
